import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { toast } from 'react-toastify';

import { HTTP_STATUS } from '@/api/ErrorCode.model.ts';
import { CONFIG } from '@/config/config.ts';
import { ToastConfig } from '@/config/ToastConfig.util.ts';
import { captureAppException } from '@/monitoring/sentry.util.ts';
import { LocalStorageKey, localStorageUtil } from '@/utils/localStorage/localStorage.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

export const useGetQueryClient = (): QueryClient => {
  const { t } = useTranslationWithPrefix('utils.toasts.fetch');

  return useMemo(() => {
    const handleMinorError = (detail?: string) => {
      toast(detail ?? t('error'), ToastConfig.error);
    };

    const handleOperationSuccess = () => {
      toast(t('success'), ToastConfig.success);
    };

    return new QueryClient({
      defaultOptions: {
        mutations: {
          retry: false,
        },
        queries: {
          refetchOnWindowFocus: false,
          retry: false,
        },
      },
      mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
          if (handle401(error, t)) {
            return;
          }

          const errorCode = (error as Partial<AxiosError<{ code?: string }>>)?.response?.data?.code;
          if (errorCode && mutation.options.meta?.locallyHandledErrorCodes?.includes(errorCode)) {
            return;
          }

          const status = (error as AxiosError)?.response?.status;
          if (
            status &&
            mutation.options.meta?.locallyHandledHttpStatuses?.includes(status)
          ) {
            return;
          }

          captureAppException(error, { contextName: 'react-query-mutation' });

          if (mutation?.options?.meta?.disableErrorAlert) {
            return;
          }
          handleError(error, handleMinorError);
        },
        onSuccess: (_data, _variables, _context, mutation) => {
          if (mutation?.options?.meta?.disableSuccessAlert) {
            return;
          }
          handleOperationSuccess();
        },
      }),
      queryCache: new QueryCache({
        onError: (error: unknown, query) => {
          if (handle401(error, t)) {
            return;
          }

          captureAppException(error, { contextName: 'react-query-query' });

          if (query?.options?.meta?.disableErrorAlert) {
            return;
          }
          handleError(error, handleMinorError);
        },
        onSuccess: (_data, query) => {
          if (query?.options?.meta?.enableSuccessAlert) {
            handleOperationSuccess();
          }
        },
      }),
    });
  }, [t]);
};

const handleError = (error: unknown, handleMinorError: (error?: string) => void) => {
  if (!error) {
    handleMinorError('Unknown Error');
    return;
  }
  if (typeof error === 'string') {
    handleMinorError(error);
    return;
  }

  if (((error as Partial<AxiosError>)?.response?.data as { message?: string })?.message) {
    handleMinorError(
      ((error as Partial<AxiosError>)?.response?.data as { message?: string })?.message,
    );
    return;
  }

  handleMinorError(
    ((error as AxiosError)?.response?.data as Partial<{ body: { detail: string } }>)?.body?.detail,
  );
};

export const LOGIN_REDIRECT_URL = CONFIG.HOST + '/' + import.meta.env.VITE_LOGIN_REDIRECT_URL;

const handle401 = (error: unknown, t: TFunction): boolean => {
  if ((error as AxiosError)?.response?.status === HTTP_STATUS.UNAUTHORIZED) {
    console.debug('401 redirect');
    t('401');
    localStorageUtil.setItem({ key: LocalStorageKey.LAST_VISITED_PAGE, value: location.href });
    globalThis.location.href = LOGIN_REDIRECT_URL;
    return true;
  }
  return false;
};
